import moment from 'moment';
import * as React from 'react';
import { CREDENTIAL_ACTIONS } from './actions/credential';
import { DASHBOARD_ACTIONS } from './actions/dashboard';
import { LOG_SEARCH_ACTIONS } from './actions/logsearch';
import { LOGS_ACTIONS } from './actions/logsources';
import { SESSION_ACTIONS } from './actions/session';
import { TICKET_ACTIONS } from './actions/ticket';
import { API_ERROR, APIError } from './APIError';
import { history } from './history';
import {
  API,
  APIV2,
  AWN_CUSTOMER_HEADER,
  AWN_USERINFO_REQUEST_HEADER,
  DEFAULT_SECURITY_SCORE,
  GENERIC_SERIES_API,
  getObjVal,
  HTTP_STATUS,
  OnboardingRequestEnum,
  PRP_API_V2,
  SALESFORCE_API,
  SUBSCRIPTIONS_API,
} from './constants';
import { dashboardSelector } from './reducers/dashboard';
import { logSearchSelector } from './reducers/logsearch';
import {
  featureFlagSelector,
  registerNewAbortController,
  sessionSelector,
} from './reducers/session';
import { AttachmentTokens } from './routes/contact/ZendeskFileUpload';
import {
  COMMON_DATA_FILTERS,
  getURLDateRangeAPI,
} from './routes/data/common/data_constants';
import {
  AgentPlatform,
  CredentialRequest,
  CredentialSchema,
  CredentialSecret,
  CustomerType,
  DeviceQuantityMap,
  Dispatch,
  DownloadLink,
  Export,
  ExportRequest,
  ExportResult,
  GlobalStatusType,
  LogLine,
  LogSourceTagUpdate,
  OBContact,
  OBSite,
  OBSites,
  OBSyncResults,
  OnboardingStatus,
  RawAgent,
  RawExport,
  RawIPListType,
  Search,
  SearchRequest,
  SensorCapabilitiesResponse,
  SensorRecommendations,
  SessionStorageKey,
  SFSubscriptions,
  SiteSensorProfiles,
  StatusColor,
  Store,
  UserType,
  VirtualLogCollector,
  VirtualSensor,
} from './types';
import { RISK_ACTIONS } from './actions/risk';
import { riskSelector } from './reducers/risk';
import {
  ONBOARDING_ACTIONS,
  RawOBContact,
  RawOBScanner,
  RawOBSensor,
  RawOBSite,
} from './actions/onboarding';
import { onboardingSelector } from './reducers/onboarding';
import onboardingUtils from './reducers/onboarding/utils';

import { TicketDetail, TicketSummary } from './types/tickets';

const DEFAULT_GLOBAL_STATUS_TIMEOUT = 6000;

type TicketPage = {
  tickets: Array<TicketSummary>;
  next?: number;
};

type IncidentNotificationType = {
  incidentNotification: string | null;
};

/**
 * Accepts a response promise from the fetch API and returns the parsed JSON from it,
 * throwing an APIError if the response is not 'ok' or is not JSON-formatted.
 * @param response
 */
const normalizeResponse = async <R extends any>(
  response: Response,
): Promise<R> => {
  let err;
  if (response.ok) {
    try {
      if (response.status !== HTTP_STATUS.NO_CONTENT) {
        return await response.json();
      } else {
        return Promise.resolve({} as R);
      }
    } catch (e) {
      // AWN-12361: Report these errors to the server
      console.warn('Non-JSON response received from API');
      throw API_ERROR.COMMUNICATION_FORMAT;
    }
  }
  // API errors should be of the format { detail: 'bad', status: 400 }
  try {
    err = await response.json();
  } catch (e) {
    // Presumably a JSON parsing error from a non-JSON response.  We don't know
    // what it contains, so safest to return a generic message rather than
    // whatever plaintext response we got from server

    // AWN-12361: Report these errors to the server
    console.warn('Non-JSON error response from server', e);
    throw API_ERROR.COMMUNICATION_FORMAT;
  }

  if (err.status === HTTP_STATUS.GATEWAY_TIMEOUT) {
    throw API_ERROR.GATEWAY_TIMEOUT;
  }

  throw new APIError(
    getObjVal(err, 'detail', API_ERROR.UNKNOWN.message),
    response.status,
  );
};

type HTTP_METHODS = 'GET' | 'PUT' | 'POST' | 'DELETE';

/**
 * Private wrapper around fetch that adds authentication token and simplifies
 * error handling.
 * @param {string} url - the URL to fetch
 * @param {HTTP_METHODS} method - the HTTP method (e.g. GET, POST)
 * @param {Object} [contents] - the request body that will be serialized to JSON
 * @param {AbortSignal} [signal] - an AbortSignal to set request's signal
 */
const doFetch = async (
  url: string,
  method: HTTP_METHODS,
  contents?: {} | null,
  signal?: AbortSignal,
  noSecondaryToken?: boolean,
): Promise<Response> => {
  const headers = new Headers({
    Authorization: `Bearer ${
      sessionStorage.getItem(SessionStorageKey.PRIMARY_TOKEN) || ''
    }`,
    [AWN_USERINFO_REQUEST_HEADER]:
      sessionStorage.getItem(SessionStorageKey.SECONDARY_TOKEN) || '',
  });
  try {
    const init: RequestInit = { method, headers };
    if (contents) {
      headers.append('Content-Type', 'application/json');
      init.body = JSON.stringify(contents);
    }
    if (signal) {
      init.signal = signal;
    }
    if (noSecondaryToken) {
      headers.delete(AWN_USERINFO_REQUEST_HEADER);
    }
    return await fetch(url, init);
  } catch (networkError) {
    throw API_ERROR.NETWORK_CONNECTIVITY;
  }
};

/**
 * @callback actionCreator
 * @param {Object} result - the response body from a successful request
 * @return {(Function | Object)} - return an action or thunk to be dispatched
 */

/**
 * @callback errorHandler
 * @param {APIError} err - the APIError resulting from the request
 * @param {Dispatch} dispatch - the redux dispatcher
 * @return void - should either handle or re-throw the error
 */

/**
 * Creates a common API-fetching thunk that will retrieve the response from the supplied
 * target (url + method + payload/contents) and dispatch the successful response using
 * the supplied action creator.  If an error occurs, common cases will be handled (e.g. 401)
 * before delegating to the supplied error handler function, and finally throwing any
 * unhandled error.
 * @param {string} url - target API endpoint
 * @param {HTTP_METHODS} method - HTTP request type to target endpoint
 * @param {Object} [contents] - optional payload for PUT/POST requests
 * @param {actionCreator} actionCreator - action creator function that will receive
 *      response body and should return plain action object or thunk to be dispatched
 * @param {errorHandler} [errorHandler] - optional error handler
 * @param {AbortController} [abortController] - optional AbortController for the fetch
 * @return {thunk}
 */
const createThunk = <R extends any>(
  url: string,
  method: HTTP_METHODS,
  contents: {} | null,
  actionCreator: (arg0: R) => any,
  errorHandler?: (err: APIError, dispatch: Dispatch) => void,
  abortController?: AbortController,
  noSecondaryToken?: boolean,
) => {
  return async (dispatch: Dispatch, getStore: () => Store) => {
    try {
      const response = await doFetch(
        url,
        method,
        contents,
        abortController?.signal,
        noSecondaryToken,
      );
      const actualCustomer = response.headers.get(AWN_CUSTOMER_HEADER),
        expectedCustomer = sessionSelector.getCustomerID(getStore());
      if (
        response.status !== HTTP_STATUS.AUTHENTICATION_FAIL &&
        !noSecondaryToken &&
        expectedCustomer &&
        expectedCustomer !== actualCustomer
      ) {
        console.warn(
          `Received response for ${actualCustomer} but customer is ${expectedCustomer}
          on request to ${url}`,
        );
        return;
      }
      const normalizedResponse = await normalizeResponse<R>(response);
      if (abortController) {
        dispatch(SESSION_ACTIONS.REMOVE_ABORT_CONTROLLER(abortController));
      }
      const action = actionCreator(normalizedResponse);
      if (typeof action === 'function') {
        return dispatch(action);
      } else {
        if (action) {
          dispatch(action);
        }
        return Promise.resolve(normalizedResponse);
      }
    } catch (err) {
      if (abortController) {
        dispatch(SESSION_ACTIONS.REMOVE_ABORT_CONTROLLER(abortController));
      }
      if (err.status === HTTP_STATUS.AUTHENTICATION_FAIL) {
        console.warn('Session expired, redirecting to login');
        // TODO: Auth0 should logout too
        dispatch(
          SESSION_ACTIONS.LOGOUT(
            'Session expired. Please log in again.',
            'warning',
          ),
        );
      } else if (errorHandler) {
        errorHandler(err, dispatch);
      } else {
        throw err;
      }
    }
  };
};

/**
 * Returns a function that accepts a TicketPage payload and returns a thunk that dispatches the tickets from
 * the payload and continues dispatching further thunks for subsequent pages
 * @param url
 * @param params
 * @param systemRequested
 */
const createTicketPageWalkingThunk = (
  url: string,
  params: URLSearchParams,
  systemRequested: boolean,
) => {
  return (page: TicketPage) => {
    return async (dispatch: Dispatch) => {
      const includeInactive =
        (params.get('include_inactive') || '').toLocaleLowerCase() === 'true';
      const nextPageNum = page.next;
      dispatch(
        TICKET_ACTIONS.UPDATE_LIST(
          page.tickets,
          includeInactive,
          params.get('after'),
          systemRequested,
        ),
      );
      if (nextPageNum) {
        const nextParams = new URLSearchParams(params.toString());
        nextParams.set('page', nextPageNum.toString());
        dispatch(
          createThunk<TicketPage>(
            `${url}?${nextParams.toString()}`,
            'GET',
            null,
            createTicketPageWalkingThunk(url, nextParams, systemRequested),
          ),
        );
      } else {
        dispatch(TICKET_ACTIONS.LIST_LOADING(false));
      }
    };
  };
};

export type InstallerType = 'adsensor' | 'vsensor_ova';

/**
 * Sets an automatically-expiring global status (toast) notification
 * @param {Dispatch} dispatch - the redux dispatcher
 * @param {string} message - message to be displayed
 * @param {StatusColor} color - status colour of the notification
 */
export const setExpiringGlobalStatus = (
  dispatch: Dispatch,
  message: React.ReactNode,
  color: StatusColor,
) => {
  const serialNumber = Date.now();
  dispatch(SESSION_ACTIONS.SET_GLOBAL_STATUS({ message, color, serialNumber }));
  setTimeout(() => {
    dispatch(SESSION_ACTIONS.CLEAR_GLOBAL_STATUS_WITH_SERIAL(serialNumber));
  }, DEFAULT_GLOBAL_STATUS_TIMEOUT);
};

/**
 * Sets an never-expiring global status (toast) notification
 * @param {Dispatch} dispatch - the redux dispatcher
 * @param {string} message - message to be displayed
 * @param {StatusColor} color - status colour of the notification
 */
export const setNeverExpiringGlobalStatus = (
  dispatch: Dispatch,
  message: React.ReactNode,
  color: StatusColor,
  title?: string,
) => {
  const serialNumber = Date.now();
  dispatch(
    SESSION_ACTIONS.SET_GLOBAL_STATUS({ message, title, color, serialNumber }),
  );
};

const doCustomerSwitch = (
  apiUrl: string,
  customer: string,
  riskID: string | undefined,
  urlAfterSwitch?: string | null,
) => {
  return async (dispatch: Dispatch) => {
    dispatch(SESSION_ACTIONS.SWITCH_CUSTOMER(null));
    try {
      const user = (await normalizeResponse(
        await doFetch(apiUrl, 'POST', { customer }),
      )) as UserType;
      if (riskID)
        user.customer = { ...user.customer, riskID: riskID } as CustomerType;
      dispatch(SESSION_ACTIONS.SWITCH_CUSTOMER(user));
      setExpiringGlobalStatus(
        dispatch,
        `You are now logged in as ${user.customer!.name}.`,
        'success',
      );
      // Kick off any other "app just started up" fetches
      dispatch(apiRequest.getTicketsWaiting());

      if (urlAfterSwitch) {
        history.push(urlAfterSwitch);
      }
      return user;
    } catch (err) {
      // if we fail to switch customers, we set an error banner, but also
      // need to find out who we actually are according to the back end,
      // so we'll call getUserInfo again just like we are reconnecting
      setExpiringGlobalStatus(
        dispatch,
        `Failed to switch customers: ${err.message}`,
        'danger',
      );
      dispatch(apiRequest.getUserInfo());
    }
  };
};

const handleGetOBError =
  (request: OnboardingRequestEnum) => (err: APIError, dispatch: Dispatch) => {
    if (err.status === HTTP_STATUS.NOT_FOUND) {
      // The backend returns Not Found for non-existing onboarding sections,
      // so we consider this a normal scenario.
      return;
    }
    setExpiringGlobalStatus(
      dispatch,
      `Error fetching onboarding ${request}.\n${err.message || ''}`,
      'danger',
    );
  };

const handleSaveOBError =
  (request: OnboardingRequestEnum, callback?: () => void) =>
  (error: APIError, dispatch: Dispatch) => {
    setExpiringGlobalStatus(
      dispatch,
      `Error saving onboarding ${request}. \n${error.message || ''}`,
      'danger',
    );
    if (callback) {
      callback();
    }
  };

/**
 * Often the consumers (UI components) need to update their *local* states
 * based on the state of an API request (success vs error). The action
 * creators should include a step to signal this.
 */
export type APIRequestCallbacks = {
  onSuccess?: () => void;
  onError?: () => void;
};

/**
 * High-level "API Requests" that handle authenticated fetching from the API, verifying responses,
 * and dispatching events to the store.
 */
export const apiRequest = {
  /**
   * Posts the specified credentials to the authentication backend with HTTP BasicAuth, returning
   * the response from that backend which will include a token and userinfo for successful authentication.
   * If the authentication fails, will dispatch a "invalid user/password" logout action.
   * @param {string} username - user name
   * @param {string} password - password
   */
  passwordLogin: (username: string, password: string = '') => {
    return async (dispatch: Dispatch) => {
      const auth = btoa(username + ':' + password);
      try {
        const response = await (normalizeResponse(
          await fetch(API.login, {
            method: 'POST',
            headers: new Headers({ Authorization: `Basic ${auth}` }),
          }),
        ) as Promise<{
          user: UserType;
          primary_token: string;
          secondary_token: string;
        }>);
        dispatch(
          SESSION_ACTIONS.LOGIN(
            response.user,
            response.primary_token,
            response.secondary_token,
          ),
        );
      } catch (err) {
        if (
          err.status === HTTP_STATUS.AUTHENTICATION_FAIL ||
          err.status === HTTP_STATUS.PERMISSION_DENIED
        ) {
          dispatch(SESSION_ACTIONS.LOGOUT(err.message, err.color));
        } else if (err.status === HTTP_STATUS.SERVER_UNAVAILABLE) {
          dispatch(SESSION_ACTIONS.LOGOUT(err.message, 'warning'));
        } else {
          dispatch(
            SESSION_ACTIONS.LOGOUT(
              'Communication error logging in. Please try again later.',
              'warning',
            ),
          );
        }
      }
    };
  },

  getUserInfo: () =>
    createThunk(
      API.userInfo,
      'GET',
      null,
      SESSION_ACTIONS.LOGIN,
      (err, dispatch) => {
        if (err?.status && err?.message) {
          dispatch(SESSION_ACTIONS.LOGOUT(err.message, 'warning'));
        } else {
          dispatch(
            SESSION_ACTIONS.LOGOUT(
              'Error reconnecting to portal. Please login again.',
              'danger',
            ),
          );
        }
      },
    ),

  // ********** Incident Information API **************************************************
  getIncidentInfo: () => {
    return createThunk(
      API.incidentNotification,
      'GET',
      null,
      (response: IncidentNotificationType) => async (dispatch: Dispatch) => {
        const { incidentNotification: message } = response;
        if (message) {
          const serialNumber: number = Date.now();
          const status: GlobalStatusType = {
            message,
            title: 'Info',
            color: 'info',
            serialNumber,
          };
          dispatch(SESSION_ACTIONS.SET_GLOBAL_STATUS(status));
        }
      },
      (error, dispatch) => {},
    );
  },

  // ********** Tickets API ***************************************************************

  /**
   * Fetches tickets waiting on the currently-logged in customer
   * @param {moment} [since]: optional date to limit fetching only waiting tickets modified after this date
   */
  getTicketsWaiting: (since?: moment.Moment | null) => {
    const params = new URLSearchParams();
    if (since && moment(since).isValid()) {
      params.set('after', since.toISOString());
    }
    return createThunk<TicketPage>(
      `${API.ticketsWaiting}?${params.toString()}`,
      'GET',
      null,
      createTicketPageWalkingThunk(
        API.ticketsWaiting,
        params,
        true,
        /* 'system requested' */
      ),
      (err, dispatch) => {
        dispatch(TICKET_ACTIONS.LIST_ERROR(err));
      },
    );
  },

  /**
   * Gets a list of tickets (requests) from backend for the specified status values (all if none specified), that
   * were updated before or after the specified dates.
   * @param {boolean} includeInactive - whether or not to include inactive tickets (status values) in results
   * @param {string} after - ISO8601 datestring that ticket 'last updated' must occur after
   * @return {Promise} chain of promises that dispatch ticket data (eventually resolves to undefined)
   */
  getTicketList: (includeInactive: boolean, after: string | null) => {
    return async (dispatch: Dispatch) => {
      const params = new URLSearchParams();
      params.set('include_inactive', includeInactive.toString());
      if (after) {
        params.set('after', after);
      }
      dispatch(TICKET_ACTIONS.LIST_LOADING(true));
      dispatch(
        createThunk<TicketPage>(
          `${API.tickets}?${params.toString()}`,
          'GET',
          null,
          createTicketPageWalkingThunk(
            API.tickets,
            params,
            false,
            /* not 'system-requested' */
          ),
          (err, dispatch) => {
            dispatch(TICKET_ACTIONS.LIST_ERROR(err));
          },
        ),
      );
    };
  },

  /**
   * Creates a new ticket
   * @param {string} subject - the title/subject of the ticket
   * @param {string} description - the main message body of the ticket
   * @param {Array<string>} attachmentTokens - string array of uploaded attachment tokens to attach to ticket
   * @param {string} internalNote - value for internal note
   * @param {string} ticketType - optional value for ticket type
   * @param {string} assigneeEmail - optional value for the assignee email
   * @param {Array<string>} emailsCCs - optional value for emails to be CCed
   * @return {Promise<TicketDetail>} - returns the newly-created ticket, which has also been dispatched to store
   */
  createTicket: (
    subject: string | null,
    description: string,
    attachmentTokens: AttachmentTokens,
    ticketType?: string,
    internalNote?: string | null,
    assigneeEmail?: string,
    emailsCCs?: string[],
    onboardingForm?: boolean,
    pmTag?: string,
    pseTag?: string,
  ) => {
    const payload: {
      subject: string;
      description: string;
      attachments?: AttachmentTokens;
      ticket_type?: string;
      internal_note?: string;
      assignee_email?: string;
      email_ccs?: string[];
      onboarding_form?: boolean;
      pm_tag?: string;
      pse_tag?: string;
    } = { subject: subject || '', description };
    if (Array.isArray(attachmentTokens) && attachmentTokens.length > 0) {
      payload.attachments = attachmentTokens;
    }
    if (ticketType) {
      payload.ticket_type = ticketType;
    }
    if (internalNote) {
      payload.internal_note = internalNote;
    }
    if (assigneeEmail) {
      payload.assignee_email = assigneeEmail;
    }
    if (emailsCCs) {
      payload.email_ccs = emailsCCs;
    }
    if (onboardingForm) {
      payload.onboarding_form = onboardingForm;
      payload.pm_tag = pmTag;
      payload.pse_tag = pseTag;
    }
    return createThunk<TicketDetail>(
      API.tickets,
      'POST',
      payload,
      TICKET_ACTIONS.UPDATE_TICKET,
    );
  },

  /**
   * Fetches complete details for a given ticket
   * @param {number} ticketId - the ID number of the ticket to get full details from
   * @return {Promise<TicketDetail>} - returns a promise that resolves to fetched ticket, which is
   *    also automatically dispatched to the ticket store
   */
  getTicketDetails: (ticketId: number) =>
    createThunk<TicketDetail>(
      `${API.tickets}/${ticketId}`,
      'GET',
      null,
      TICKET_ACTIONS.UPDATE_TICKET,
      (err: APIError, dispatch) => {
        dispatch(TICKET_ACTIONS.DETAIL_ERROR(err));
      },
    ),

  /**
   * Adds a comment to an existing request
   * @param ticketId {number} - id to ticket to reply to
   * @param commentBody {string} - text contents of comment to add to request
   * @param attachmentTokens {[string]} - string array of uploaded attachment tokens to include with comment
   * @param solved {boolean} - whether or not user considers request solved
   * @return {Promise<TicketDetail>}
   */
  addRequestComment: (
    ticketId: number,
    commentBody: string | null,
    attachmentTokens: Array<string> | null,
    solved: boolean | null,
  ) => {
    let payload: { comment: { [key: string]: any } } = { comment: {} };
    if (commentBody) {
      payload.comment['body'] = commentBody;
    }
    if (Array.isArray(attachmentTokens) && attachmentTokens.length > 0) {
      payload.comment['attachments'] = attachmentTokens;
    }
    if (solved !== null && solved !== undefined) {
      payload.comment['solved'] = solved;
    }
    return createThunk<TicketDetail>(
      `${API.tickets}/${ticketId}`,
      'POST',
      payload,
      TICKET_ACTIONS.UPDATE_TICKET,
    );
  },

  // ********** Secrets-Schema API ***************************************************************

  /**
   * Fetches the secret schemas that are to be displayed in the portal.
   * @return {Promise<void>}
   */
  getSecretsSchema: () => {
    return async (dispatch: Dispatch) => {
      dispatch(CREDENTIAL_ACTIONS.SET_SCHEMA_LOADING(true));
      dispatch(
        createThunk<{ schemas: Array<CredentialSchema> }>(
          API.credentialSchema,
          'GET',
          null,
          (res) => CREDENTIAL_ACTIONS.SET_SCHEMA(res.schemas),
          (err, dispatch) => {
            // AWN-12361: Report these errors to the server
            dispatch(
              CREDENTIAL_ACTIONS.SET_ERROR(
                API_ERROR.COMMUNICATION_FORMAT, // the 'get schema' endpoint never throws an exception, so we must not have been able to talk to it
              ),
            );
          },
        ),
      );
    };
  },

  // ********** Customer Switch (formerly Impersonation) API ******************************

  /**
   * Get a list of names and IDs of the customers
   * @return Promise<[customers]>
   */
  getCustomerNames: () =>
    createThunk(
      API.customerSwitch,
      'GET',
      null,
      SESSION_ACTIONS.SET_CUSTOMER_LIST,
      (err, dispatch) => {
        setExpiringGlobalStatus(
          dispatch,
          'Failed to retrieve list of available customer names to switch to',
          'danger',
        );
      },
    ),

  /**
   * Get a list of names and IDs of the end customers the current MSP user can switch to
   * @return Promise<[customers]>
   */
  getMspEndCustomerNames: () =>
    createThunk(
      API.mspEndCustomers,
      'GET',
      null,
      SESSION_ACTIONS.SET_CUSTOMER_LIST,
      (err, dispatch) => {
        setExpiringGlobalStatus(
          dispatch,
          'Failed to retrieve list of available customer names to switch to',
          'danger',
        );
      },
    ),

  /**
   * Request switching the currently logged-in user to the specified customer
   * @param {string} id - customer ID to switch to
   * @param {string | undefined} riskID - riskID of next customer
   * @param {string} [urlAfterSwitch] - URL to push to history after switch
   * @return {Promise<UserType | undefined>} - a user including the new customer on success
   *   or undefined on error
   */
  switchCustomer: (
    id: string,
    riskID: string | undefined,
    urlAfterSwitch?: string | null,
  ) => doCustomerSwitch(API.customerSwitch, id, riskID, urlAfterSwitch),

  switchMSPCustomer: (
    id: string,
    riskID: string | undefined,
    urlAfterSwitch: string | null,
  ) => doCustomerSwitch(API.mspEndCustomers, id, riskID, urlAfterSwitch),

  // ********** Metrics API *********************************************************

  getDashboardData: () => {
    return (dispatch: Dispatch, getStore: () => Store) => {
      let fetches = [];
      const store = getStore();

      // Only fetch if there's a customer set
      if (!sessionSelector.getCustomerID(store)) {
        return;
      }

      // Only fetch if our data is too old (or hasn't been fetched), and when
      // fetching only show loading indicators if we have no data
      if (dashboardSelector.isExpiredSummary(store)) {
        if (
          dashboardSelector.getSecurityScore(store) === DEFAULT_SECURITY_SCORE
        ) {
          dispatch(DASHBOARD_ACTIONS.SET_DASHBOARD_LOADING('summary'));
        }
        fetches.push(
          createThunk(
            API.metricsSummary,
            'GET',
            null,
            DASHBOARD_ACTIONS.SET_SUMMARY_DATA,
            (err, dispatch) => {
              dispatch(DASHBOARD_ACTIONS.SET_SUMMARY_ERROR(err));
            },
          ),
        );
      }

      if (dashboardSelector.isExpiredIncidents(store)) {
        if (dashboardSelector.getInvestigationCount(store) === null) {
          dispatch(DASHBOARD_ACTIONS.SET_DASHBOARD_LOADING('incidents'));
        }
        const orgId = sessionSelector.getRiskID(store) || '';
        const pod = sessionSelector.getCustomerPod(store) || '';
        fetches.push(
          createThunk(
            API.metricsIncidents(orgId, pod),
            'GET',
            null,
            DASHBOARD_ACTIONS.SET_INCIDENTS_DATA,
            (err, dispatch) => {
              dispatch(DASHBOARD_ACTIONS.SET_INCIDENTS_ERROR(err));
            },
            undefined,
            true,
          ),
        );
      }

      if (dashboardSelector.isExpiredCountryFlows(store)) {
        if (dashboardSelector.getTopCountries(store).length === 0) {
          dispatch(DASHBOARD_ACTIONS.SET_DASHBOARD_LOADING('countries'));
        }
        fetches.push(
          createThunk(
            API.metricsCountryFlows,
            'GET',
            null,
            DASHBOARD_ACTIONS.SET_COUNTRY_FLOWS_DATA,
            (err, dispatch) => {
              dispatch(DASHBOARD_ACTIONS.SET_COUNTRY_FLOWS_ERROR(err));
            },
          ),
        );
      }

      if (riskSelector.isExpiredAgents(store)) {
        if (Object.keys(riskSelector.getAgentMap(store)).length === 0) {
          dispatch(RISK_ACTIONS.SET_AGENTS_LOADING(true));
        }
        const abortController = registerNewAbortController(dispatch);
        fetches.push(
          createThunk(
            API.agents,
            'GET',
            null,
            RISK_ACTIONS.SET_AGENT_MAP,
            (err, dispatch) => {
              dispatch(RISK_ACTIONS.SET_AGENTS_ERROR(err));
            },
            abortController,
          ),
        );
      }

      // invoke all dashboard data fetches in parallel
      return Promise.all(fetches.map((f) => dispatch(f)));
    };
  },

  getAgentList: () => {
    return async (dispatch: Dispatch) => {
      const abortController = registerNewAbortController(dispatch);
      dispatch(RISK_ACTIONS.SET_AGENTS_LOADING(true));
      dispatch(
        createThunk(
          API.agents,
          'GET',
          null,
          RISK_ACTIONS.SET_AGENT_MAP,
          (err, dispatch) => {
            dispatch(RISK_ACTIONS.SET_AGENTS_ERROR(err));
          },
          abortController,
        ),
      );
    };
  },

  getAgentsByIds: (agentIds: string[]) => {
    return async (dispatch: Dispatch) => {
      const abortController = registerNewAbortController(dispatch);
      dispatch(RISK_ACTIONS.SET_AGENTS_LOADING(true));
      dispatch(
        createThunk(
          API.agents,
          'POST',
          { agentIds: agentIds.join(',') },
          RISK_ACTIONS.UPDATE_AGENT_MAP,
          (err, dispatch) => {
            dispatch(RISK_ACTIONS.SET_AGENTS_ERROR(err));
          },
          abortController,
        ),
      );
    };
  },

  getSensorMetrics: () => {
    return async (dispatch: Dispatch) => {
      dispatch(DASHBOARD_ACTIONS.SET_DASHBOARD_LOADING('sensors'));
      dispatch(
        createThunk(
          API.metricsSensors,
          'GET',
          null,
          DASHBOARD_ACTIONS.SET_SENSOR_METRICS,
          (err, dispatch) => {
            dispatch(DASHBOARD_ACTIONS.SET_SENSOR_METRICS_ERROR(err));
          },
        ),
      );
    };
  },

  setGlobalStatus: (
    message: React.ReactNode,
    color: StatusColor,
    title?: string,
    neverExpiring?: boolean,
  ) => {
    return async (dispatch: Dispatch) => {
      neverExpiring
        ? setNeverExpiringGlobalStatus(dispatch, message, color, title)
        : setExpiringGlobalStatus(dispatch, message, color);
    };
  },

  // ********** Log Source API ******************************************************

  /**
   * Get list of available log sources
   */
  getLogSources: () => {
    return async (dispatch: Dispatch) => {
      dispatch(LOGS_ACTIONS.SET_LOG_SOURCES_LOADING(true));
      dispatch(
        createThunk(
          API.logSources,
          'GET',
          null,
          LOGS_ACTIONS.SET_LOG_SOURCES,
          (err, dispatch) => {
            dispatch(LOGS_ACTIONS.SET_LOG_SOURCES_LOADING(false));
            setExpiringGlobalStatus(
              dispatch,
              'Unable to retrieve the available log sources. You can still perform a search.',
              'warning',
            );
          },
        ),
      );
    };
  },

  /**
   * Update log source tag
   */
  updateLogSourceTag: (
    payload: LogSourceTagUpdate,
    onSuccess?: (arg0: any) => any,
  ) => {
    return async (dispatch: Dispatch) => {
      dispatch(
        createThunk(
          API.logSourceTag,
          'POST',
          payload,
          () => onSuccess,
          (err, dispatch) => {
            dispatch(LOGS_ACTIONS.SET_LOG_SOURCES_LOADING(false));
            setExpiringGlobalStatus(
              dispatch,
              `Unable to ${payload.action} the tag "${payload.tag}": ${err.message}`,
              'warning',
            );
          },
        ),
      );
    };
  },

  /**
   * Gets the list of reserved system-generated tags (for configured parser)
   */
  getReservedLogSourceTags: () =>
    createThunk(
      API.logParserTag,
      'GET',
      null,
      LOGS_ACTIONS.SET_RESERVED_TAGS,
      (err, dispatch) => {
        // we presently don't care if we fail to retrieve the reserved tags; it just means client-side
        // validation will be disabled and we'll have to rely on server-side rejection of the tag
        dispatch(LOGS_ACTIONS.SET_RESERVED_TAGS_LOADING(false));
      },
    ),

  // ********** Log Search API ******************************************************

  /**
   * Get list of available searches
   */
  getLogSearches: () => {
    return async (dispatch: Dispatch) => {
      dispatch(LOG_SEARCH_ACTIONS.SET_LIST_LOADING());
      dispatch(
        createThunk(
          API.logSearch,
          'GET',
          null,
          LOG_SEARCH_ACTIONS.SET_SEARCHES_LIST,
          (err, dispatch) => {
            dispatch(LOG_SEARCH_ACTIONS.SET_SEARCH_LIST_ERROR(err));
          },
        ),
      );
    };
  },

  /**
   * Returns the timestamp of the earliest/oldest available raw log line for the customer
   */
  getEarliestRecord: () =>
    createThunk(
      API.logSearchEarliestRecord,
      'GET',
      null,
      LOG_SEARCH_ACTIONS.SET_EARLIEST_RECORD,
      (err, dispatch) => {
        setExpiringGlobalStatus(
          dispatch,
          'Unable to retrieve the date of your earliest available data in the system. You can still perform a search.',
          'warning',
        );
      },
    ),

  /**
   * Retrieve the metadata (query criteria, time range, result count, histogram, etc)
   * for the specified log search
   * @param {string} searchId - UUID of the search
   * @return {Promise<{start: *, end: *, query: *}>}
   */
  getLogSearch: (searchId: string) =>
    createThunk(
      `${API.logSearch}/${searchId}`,
      'GET',
      null,
      LOG_SEARCH_ACTIONS.SET_SEARCH,
      (err, dispatch) => {
        if (err.status === HTTP_STATUS.NOT_FOUND) {
          dispatch(LOG_SEARCH_ACTIONS.CLEAR_SEARCH_EXCEPT_REQUEST());
          dispatch(
            LOG_SEARCH_ACTIONS.SET_ERROR(
              new APIError(
                `There is no search with ID '${searchId}'.  It may have been deleted or expired.`,
                HTTP_STATUS.NOT_FOUND,
              ),
            ),
          );
        } else {
          dispatch(LOG_SEARCH_ACTIONS.SET_ERROR(err));
        }
      },
    ),

  /**
   * Fetches a page of results (raw log lines) for the supplied search. The
   * page number is an integer starting at 1 (not 0).
   * @param {string} searchId - UUID of the search
   * @param {number} pageNum - page offset where 1 is most-recent
   */
  getLogSearchResultPage: (searchId: string, pageNum: number) => {
    return async (dispatch: Dispatch, getStore: () => Store) => {
      const resultPage = logSearchSelector.getSearchResultPage(getStore());
      dispatch(
        LOG_SEARCH_ACTIONS.SET_RESULT_PAGE(searchId, {
          ...resultPage,
          pageNum,
          loading: true,
        }),
      );
      dispatch(
        createThunk<Array<LogLine>>(
          `${API.logSearchResults}/${searchId}?page=${pageNum}`,
          'GET',
          null,
          (response) => {
            dispatch(LOG_SEARCH_ACTIONS.SET_ERROR(null));
            return LOG_SEARCH_ACTIONS.SET_RESULT_PAGE(searchId, {
              pageNum,
              loading: false,
              results: response,
            });
          },
          (err, dispatch) => {
            dispatch(LOG_SEARCH_ACTIONS.SET_ERROR(err));
            dispatch(
              LOG_SEARCH_ACTIONS.SET_RESULT_PAGE(searchId, {
                pageNum,
                loading: false,
                results: null,
              }),
            );
          },
        ),
      );
    };
  },

  /**
   * Initiate a new raw log search based on values set in store
   * @param request {SearchRequest} - the search request
   */
  startLogSearch: (request: SearchRequest) => {
    return async (dispatch: Dispatch) => {
      dispatch(LOG_SEARCH_ACTIONS.CLEAR_SEARCH_EXCEPT_REQUEST());
      dispatch(
        createThunk<{ id: string }>(
          API.logSearch,
          'POST',
          {
            query: request.query,
            end_time: request.end.toISOString(),
            start_time: request.start.toISOString(),
            case_sensitive: request.caseSensitive,
            log_sources_tags: request.logSourcesTags,
          },
          (response) => {
            dispatch(LOG_SEARCH_ACTIONS.SET_SEARCH_ID(response.id));
          },
          (err, dispatch) => {
            dispatch(LOG_SEARCH_ACTIONS.SET_ERROR(err));
          },
        ),
      );
    };
  },

  /**
   * Cancels the active raw log search
   */
  cancelLogSearch: (searchId: string) => {
    return async (dispatch: Dispatch) => {
      dispatch(
        createThunk(
          `${API.logSearch}/${searchId}`,
          'DELETE',
          null,
          () => {
            dispatch(LOG_SEARCH_ACTIONS.CLEAR_SEARCH_EXCEPT_REQUEST());
            setExpiringGlobalStatus(
              dispatch,
              `Search ${searchId} was cancelled.`,
              'success',
            );
          },
          (err, dispatch) => {
            dispatch(LOG_SEARCH_ACTIONS.CLEAR_SEARCH_EXCEPT_REQUEST());
            setExpiringGlobalStatus(
              dispatch,
              err.message ||
                `Unable to cancel search ${searchId}. It can be deleted from 'Search Activity' later.`,
              'warning',
            );
          },
        ),
      );
    };
  },

  /**
   * Deletes the specified raw log search and re-fetch searches list
   * @param {string} searchId - the UUID of the search to delete
   * @param {any} onSuccess - actions to dispatch if the export succeeds
   */
  deleteLogSearch: (
    searchId: string,
    onSuccess?: (arg0: any, arg1: () => Store) => any,
  ) => {
    return async (dispatch: Dispatch) => {
      dispatch(
        createThunk(
          `${API.logSearch}/${searchId}`,
          'DELETE',
          null,
          () => onSuccess,
          (err, dispatch) => {
            setExpiringGlobalStatus(
              dispatch,
              err.message ||
                `Unable to delete search ${searchId}. Please try again later.`,
              'warning',
            );
          },
        ),
      );
    };
  },

  /**
   * Retry a search by creating a duplicate search and then deleting the original one
   * made for LogSearchActivity
   * @param search {Search} - the search to retry
   */
  retryLogSearch: (search: Search) => {
    return async (dispatch: Dispatch) => {
      const { id, request } = search;
      const errorHandler = (err: APIError, dispatch: Dispatch) => {
        setExpiringGlobalStatus(
          dispatch,
          err.message ||
            `Unable to retry search ${id}. Please try again later.`,
          'warning',
        );
      };
      dispatch(
        createThunk<{ id: string }>(
          API.logSearch,
          'POST',
          {
            query: request.query,
            end_time: request.end.toISOString(),
            start_time: request.start.toISOString(),
            case_sensitive: request.caseSensitive,
            log_sources_tags: request.logSourcesTags,
          },
          (response) => {
            if (response.id) {
              return async (dispatch: Dispatch) => {
                dispatch(
                  createThunk(
                    `${API.logSearch}/${id}`,
                    'DELETE',
                    null,
                    () => apiRequest.getLogSearches(),
                    errorHandler,
                  ),
                );
              };
            }
          },
          errorHandler,
        ),
      );
    };
  },

  /**
   * Initiate a new export given a search ID
   * @param {ExportRequest} exportRequest - the data required to start an export
   * @param {any} onSuccess - actions to dispatch if the export succeeds
   */
  startExportForSearch: (
    exportRequest: ExportRequest,
    onSuccess?: (arg0: any, arg1: () => Store) => any,
  ) => {
    return async (dispatch: Dispatch, getStore: () => Store) => {
      const activeSearchId = logSearchSelector.getSearchId(getStore());
      const isLogSearchV2 = featureFlagSelector.logSearchV2(getStore());

      const content = isLogSearchV2
        ? {
            search_id: exportRequest.searchId,
          }
        : {
            search_id: exportRequest.searchId,
            data_format: exportRequest.dataFormat || 'csv',
            archive_format: exportRequest.archiveFormat || 'zip',
            split_by_timeframe: exportRequest.splitByTimeframe,
            log_sources_info: exportRequest.logSourcesInfo,
          };

      dispatch(
        createThunk<RawExport>(
          isLogSearchV2
            ? `${APIV2.logSearch}/export`
            : `${API.logSearch}/${exportRequest.searchId}/exports`,
          'POST',
          content,
          (response) => {
            if (response.search_id === activeSearchId) {
              dispatch(LOG_SEARCH_ACTIONS.SET_EXPORT(response));
            }
            return onSuccess;
          },
          (err, dispatch) => {
            setExpiringGlobalStatus(dispatch, err.message, 'danger');
          },
        ),
      );
    };
  },

  /**
   * Retrieve the updated export object
   */
  getExportStatus: (searchExport: Export) => {
    return createThunk(
      `${API.logSearch}/${searchExport.searchId}/exports/${searchExport.id}`,
      'GET',
      null,
      LOG_SEARCH_ACTIONS.SET_EXPORT,
      (err, dispatch) => {
        dispatch(LOG_SEARCH_ACTIONS.SET_ERROR(err));
      },
    );
  },

  // ********** Onboarding API ************************************************************
  getOBContacts: () => {
    return (dispatch: Dispatch) => {
      dispatch(ONBOARDING_ACTIONS.SET_CONTACTS_LOADING(true));
      dispatch(
        createThunk(
          API.onboarding.contactsV2.base,
          'GET',
          null,
          ONBOARDING_ACTIONS.SET_CONTACTS,
          (error) => {
            dispatch(ONBOARDING_ACTIONS.SET_CONTACTS_LOADING(false));
            handleGetOBError(OnboardingRequestEnum.CONTACTS);
          },
        ),
      );
    };
  },
  updateOBSiteSection: (siteSection: OBSites) => (dispatch: Dispatch) =>
    dispatch(
      createThunk(
        API.onboarding.sites,
        'PUT',
        onboardingUtils.sitesToAPI(siteSection),
        ONBOARDING_ACTIONS.SET_SITES,
        (error) => {
          setExpiringGlobalStatus(
            dispatch,
            `Error updating sites section\n${error.message || ''}`,
            'danger',
          );
        },
      ),
    ),
  createOBContact:
    (contact: OBContact, callbacks?: APIRequestCallbacks) =>
    (dispatch: Dispatch) =>
      dispatch(
        createThunk(
          API.onboarding.contactsV2.base,
          'POST',
          onboardingUtils.contactToAPI('', contact),
          (response: { contact: RawOBContact }) => {
            dispatch(
              ONBOARDING_ACTIONS.SET_CONTACT(
                response.contact.id,
                onboardingUtils.contactFromAPI(response.contact),
              ),
            );
            dispatch(
              ONBOARDING_ACTIONS.SET_LAST_UPDATED_CONTACTS(
                response.contact.id,
                onboardingUtils.contactFromAPI(response.contact),
              ),
            );
            callbacks?.onSuccess?.();
          },
          (err: Error) => {
            setExpiringGlobalStatus(
              dispatch,
              `Error adding contact ${contact.firstName} ${contact.lastName}\n${
                err.message || ''
              }`,
              'danger',
            );
            callbacks?.onError?.();
          },
        ),
      ),

  updateOBContact:
    (uuid: string, contact: OBContact, callbacks?: APIRequestCallbacks) =>
    (dispatch: Dispatch) =>
      dispatch(
        createThunk(
          `${API.onboarding.contactsV2.base}/${uuid}`,
          'PUT',
          onboardingUtils.contactToAPI('', contact),
          (response: { contact: RawOBContact }) => {
            dispatch(
              ONBOARDING_ACTIONS.SET_LAST_UPDATED_CONTACTS(
                uuid,
                onboardingUtils.contactFromAPI(response.contact),
              ),
            );
            callbacks?.onSuccess?.();
          },
          (err: Error) => {
            setExpiringGlobalStatus(
              dispatch,
              `Error updating contact ${contact.firstName} ${
                contact.lastName
              }. ${err.message || ''} Reverting to last updated state.`,
              'danger',
            );
            dispatch(ONBOARDING_ACTIONS.REVERT_CONTACTS());
            callbacks?.onError?.();
          },
        ),
      ),

  toggleOBContactsComplete:
    (callbacks?: APIRequestCallbacks) =>
    (dispatch: Dispatch, getStore: () => Store) => {
      const wasComplete = onboardingSelector.getContactsCompleted(getStore());
      dispatch(
        createThunk(
          wasComplete
            ? API.onboarding.contactsV2.markIncomplete
            : API.onboarding.contactsV2.markComplete,
          'PUT',
          null,
          () => {
            dispatch(ONBOARDING_ACTIONS.TOGGLE_COMPLETE('contactsSection'));
            callbacks?.onSuccess?.();
          },
          (err: Error) => {
            setExpiringGlobalStatus(
              dispatch,
              `Error updating contact section status. ${err.message || ''}`,
              'danger',
            );
            callbacks?.onError?.();
          },
        ),
      );
    },

  getOBInternetExposure: () => {
    return createThunk(
      API.onboarding.external,
      'GET',
      null,
      ONBOARDING_ACTIONS.SET_INTERNET_EXPOSURE,
      handleGetOBError(OnboardingRequestEnum.INTERNET_EXPOSURE),
    );
  },

  saveOBInternetExposure: () => {
    return (dispatch: Dispatch, getStore: () => Store) => {
      const internetExposure = onboardingSelector.getInternetExposure(
        getStore(),
      );
      dispatch(
        createThunk(
          API.onboarding.external,
          'PUT',
          onboardingUtils.internetExposureToAPI(internetExposure),
          ONBOARDING_ACTIONS.SET_INTERNET_EXPOSURE,
          handleSaveOBError(OnboardingRequestEnum.INTERNET_EXPOSURE),
        ),
      );
    };
  },

  getOBSites: () => {
    return createThunk(
      API.onboarding.sites,
      'GET',
      null,
      ONBOARDING_ACTIONS.SET_SITES,
      handleGetOBError(OnboardingRequestEnum.SITES),
    );
  },

  createOBSite:
    (site: OBSite, callbacks?: APIRequestCallbacks) => (dispatch: Dispatch) =>
      dispatch(
        createThunk(
          API.onboarding.sitesV2.base,
          'POST',
          onboardingUtils.siteToAPI('', site),
          (response: { site: RawOBSite }) => {
            dispatch(
              ONBOARDING_ACTIONS.SET_SITE(
                response.site.id,
                onboardingUtils.siteFromAPI(response.site),
              ),
            );
            callbacks?.onSuccess?.();
          },
          (err: Error) => {
            setExpiringGlobalStatus(
              dispatch,
              `Error adding site ${site.name}\n${err.message || ''}`,
              'danger',
            );
            callbacks?.onError?.();
          },
        ),
      ),

  updateOBSite:
    (uuid: string, site: OBSite, callbacks?: APIRequestCallbacks) =>
    (dispatch: Dispatch) =>
      dispatch(
        createThunk(
          `${API.onboarding.sitesV2.base}/${uuid}`,
          'PUT',
          onboardingUtils.siteToAPI(uuid, site),
          (response: { site: RawOBSite }) => {
            dispatch(
              ONBOARDING_ACTIONS.SET_SITE(
                uuid,
                onboardingUtils.siteFromAPI(response.site),
              ),
            );
            callbacks?.onSuccess?.();
          },
          (err: Error) => {
            setExpiringGlobalStatus(
              dispatch,
              `Error updating site ${site.name}. ${err.message || ''}`,
              'danger',
            );
            callbacks?.onError?.();
          },
        ),
      ),

  toggleOBSitesComplete:
    (callbacks?: APIRequestCallbacks) =>
    (dispatch: Dispatch, getStore: () => Store) => {
      const wasComplete = onboardingSelector.getSitesCompleted(getStore());
      dispatch(
        createThunk(
          wasComplete
            ? API.onboarding.sitesV2.markIncomplete
            : API.onboarding.sitesV2.markComplete,
          'PUT',
          null,
          () => {
            dispatch(ONBOARDING_ACTIONS.TOGGLE_COMPLETE('sitesSection'));
            callbacks?.onSuccess?.();
          },
          (err: Error) => {
            setExpiringGlobalStatus(
              dispatch,
              `Error updating site section status. ${err.message || ''}`,
              'danger',
            );
            callbacks?.onError?.();
          },
        ),
      );
    },

  deleteOBSite: (siteId: string) => {
    return (dispatch: Dispatch, getStore: () => Store) => {
      const customerId = sessionSelector.getCustomerID(getStore());
      dispatch(
        createThunk(
          API.onboarding.sitesV2.prpBase(customerId!, siteId),
          'DELETE',
          null,
          () => ONBOARDING_ACTIONS.DELETE_SITE(siteId),
          () => {
            setExpiringGlobalStatus(dispatch, `Error deleting site`, 'danger');
          },
        ),
      );
    };
  },

  getOBSensors: () => {
    return (dispatch: Dispatch) => {
      dispatch(ONBOARDING_ACTIONS.SET_SENSORS_LOADING(true));
      dispatch(
        createThunk(
          API.onboarding.sensors,
          'GET',
          null,
          ONBOARDING_ACTIONS.SET_SENSORS,
          (error) => {
            dispatch(ONBOARDING_ACTIONS.SET_SENSORS_LOADING(false));
            handleGetOBError(OnboardingRequestEnum.SENSORS);
          },
        ),
      );
    };
  },

  saveOBSensors: (callback?: APIRequestCallbacks) => {
    return (dispatch: Dispatch, getStore: () => Store) => {
      const sensorsSection = onboardingSelector.getSensorsSection(getStore());
      dispatch(
        createThunk(
          API.onboarding.sensors,
          'PUT',
          onboardingUtils.sensorsToAPI(sensorsSection),
          (response: { sensors: RawOBSensor[]; completed: boolean }) => {
            dispatch(ONBOARDING_ACTIONS.SET_SENSORS(response));
            callback?.onSuccess?.();
          },
          (error) => {
            dispatch(ONBOARDING_ACTIONS.SET_SENSORS_LOADING(false));
            handleSaveOBError(OnboardingRequestEnum.SENSORS, callback?.onError);
          },
        ),
      );
    };
  },

  getOBScanners: () => {
    return createThunk(
      API.onboarding.scanners,
      'GET',
      null,
      ONBOARDING_ACTIONS.SET_SCANNERS,
      handleGetOBError(OnboardingRequestEnum.SCANNERS),
    );
  },

  saveOBScanners: (callback?: APIRequestCallbacks) => {
    return (dispatch: Dispatch, getStore: () => Store) => {
      const scanners = onboardingSelector.getScanners(getStore());
      const completed = onboardingSelector.getScannersCompleted(getStore());
      const loading = onboardingSelector.getScannersSectionLoading(getStore());
      dispatch(
        createThunk(
          API.onboarding.scanners,
          'PUT',
          {
            ...onboardingUtils.scannersToAPI({ scanners, completed, loading }),
          },
          (response: { scanners: RawOBScanner[]; completed: boolean }) => {
            dispatch(ONBOARDING_ACTIONS.SET_SCANNERS(response));
            callback?.onSuccess?.();
          },
          handleSaveOBError(OnboardingRequestEnum.SCANNERS, callback?.onError),
        ),
      );
    };
  },

  getOBCloudSensors: () => {
    return createThunk(
      API.onboarding.cloudSensors,
      'GET',
      null,
      ONBOARDING_ACTIONS.SET_CLOUD_SENSORS,
      handleGetOBError(OnboardingRequestEnum.CLOUD),
    );
  },

  saveOBCloudSensors: () => {
    return (dispatch: Dispatch, getStore: () => Store) => {
      const cloudSensors = onboardingSelector.getCloudSensors(getStore());
      dispatch(
        createThunk(
          API.onboarding.cloudSensors,
          'PUT',
          onboardingUtils.cloudSensorsToAPI(cloudSensors),
          () => null, // No-op
          handleSaveOBError(OnboardingRequestEnum.CLOUD),
        ),
      );
    };
  },

  getOBVirtualScanner: () => {
    return createThunk(
      API.onboarding.virtualScanner,
      'GET',
      null,
      ONBOARDING_ACTIONS.SET_VIRTUAL_SCANNER,
      handleGetOBError(OnboardingRequestEnum.VIRTUAL),
    );
  },

  saveOBVirtualScanner: () => {
    return (dispatch: Dispatch, getStore: () => Store) => {
      const config = onboardingSelector.getVirtualScanner(getStore());
      dispatch(
        createThunk(
          API.onboarding.virtualScanner,
          'PUT',
          onboardingUtils.virtualScannerToAPI(config),
          ONBOARDING_ACTIONS.SET_VIRTUAL_SCANNER,
          handleSaveOBError(OnboardingRequestEnum.VIRTUAL),
        ),
      );
    };
  },

  getOBAvailableEndpoints: () => {
    return createThunk(
      API.onboarding.availableEndpoints,
      'GET',
      null,
      ONBOARDING_ACTIONS.SET_ONBOARDING_AVAILABLE_ENDPOINTS,
      handleGetOBError(OnboardingRequestEnum.AVAILABLE_ENDPOINT),
    );
  },

  getOBEndpointSection: () => {
    return createThunk(
      API.onboarding.endpoint,
      'GET',
      null,
      ONBOARDING_ACTIONS.SET_ENDPOINT,
      handleGetOBError(OnboardingRequestEnum.ENDPOINT),
    );
  },

  saveOBEndpointSection: () => {
    return (dispatch: Dispatch, getStore: () => Store) => {
      const config = onboardingSelector.getEndpointConfig(getStore());
      dispatch(
        createThunk(
          API.onboarding.endpoint,
          'PUT',
          onboardingUtils.endpointConfigToAPI(config),
          ONBOARDING_ACTIONS.SET_ENDPOINT,
          handleSaveOBError(OnboardingRequestEnum.ENDPOINT),
        ),
      );
    };
  },

  getOBAvailableVirtualSensors: () => {
    return createThunk(
      API.onboarding.availableVirtualSensors,
      'GET',
      null,
      ONBOARDING_ACTIONS.SET_ONBOARDING_AVAILABLE_VIRTUAL_SENSORS,
      handleGetOBError(OnboardingRequestEnum.AVAILABLE_VIRTUAL_SENSORS),
    );
  },

  getOBVirtualSensorSection: () => {
    return createThunk(
      API.onboarding.virtualSensor,
      'GET',
      null,
      ONBOARDING_ACTIONS.SET_VIRTUAL_SENSOR,
      handleGetOBError(OnboardingRequestEnum.VIRTUAL_SENSOR),
    );
  },

  saveOBVirtualSensorSection: () => {
    return (dispatch: Dispatch, getStore: () => Store) => {
      const config = onboardingSelector.getVirtualSensorConfig(getStore());
      dispatch(
        createThunk(
          API.onboarding.virtualSensor,
          'PUT',
          onboardingUtils.virtualSensorConfigToAPI(config),
          ONBOARDING_ACTIONS.SET_VIRTUAL_SENSOR,
          handleSaveOBError(OnboardingRequestEnum.VIRTUAL_SENSOR),
        ),
      );
    };
  },

  saveDataEntryCompleted: () => {
    return (dispatch: Dispatch) => {
      dispatch(
        createThunk(
          API.onboarding.completeDataEntry,
          'POST',
          true,
          (response: OnboardingStatus) => {
            dispatch(SESSION_ACTIONS.SET_ONBOARDING_STATUS(response));
            setExpiringGlobalStatus(
              dispatch,
              `Successfully submitted onboarding checklist.`,
              'success',
            );
          },
          handleSaveOBError(OnboardingRequestEnum.COMPLETE_FORM),
        ),
      );
    };
  },

  updateAllotment: (model: string, quantity: number) => (dispatch: Dispatch) =>
    dispatch(
      createThunk(
        API.onboarding.allotment,
        'POST',
        {
          model,
          quantity,
        },
        (response: DeviceQuantityMap) => {
          dispatch(
            ONBOARDING_ACTIONS.SET_ONBOARDING_AVAILABLE_HARDWARE_QUANTITY(
              response,
            ),
          );
          setExpiringGlobalStatus(
            dispatch,
            `Successfully updated allotment.`,
            'success',
          );
        },
        (err, dispatch) => {
          setExpiringGlobalStatus(
            dispatch,
            `Error updating customer allotment. ${err.message}`,
            'danger',
          );
        },
      ),
    ),

  doOBSync: () => {
    return (dispatch: Dispatch) => {
      dispatch(
        createThunk(
          API.onboarding.doSync,
          'POST',
          null,
          (response: OBSyncResults) => {
            dispatch(ONBOARDING_ACTIONS.SET_SYNC_RESULTS(response));
          },
        ),
      );
    };
  },

  getOnboardingTeamContacts: () => {
    return createThunk(
      API.onboarding.onboardingTeamContacts,
      'GET',
      null,
      ONBOARDING_ACTIONS.SET_ONBOARDING_TEAM_CONTACTS,
      handleGetOBError(OnboardingRequestEnum.TEAM_CONTACTS),
    );
  },

  getOnboardingProducts: () => {
    return createThunk(
      API.onboarding.products,
      'GET',
      null,
      ONBOARDING_ACTIONS.SET_ONBOARDING_PRODUCTS,
      handleGetOBError(OnboardingRequestEnum.PRODUCTS),
    );
  },

  getOnboardingAvailableHardware: () => {
    return createThunk(
      API.onboarding.availableHardware,
      'GET',
      null,
      ONBOARDING_ACTIONS.SET_ONBOARDING_AVAILABLE_HARDWARE,
      handleGetOBError(OnboardingRequestEnum.HARDWARE),
    );
  },

  getOnboardingAvailableGenericSensors: () => {
    return createThunk(
      GENERIC_SERIES_API.availableSeries,
      'GET',
      null,
      ONBOARDING_ACTIONS.SET_ONBOARDING_AVAILABLE_GENERIC_SENSORS,
      handleGetOBError(OnboardingRequestEnum.AVAILABLE_GENERIC_SENSORS),
    );
  },

  getSensorCapabilities: (seriesId: string) => {
    return (dispatch: Dispatch) => {
      dispatch(
        createThunk(
          GENERIC_SERIES_API.capabilities(seriesId),
          'GET',
          null,
          (response: SensorCapabilitiesResponse) => {
            dispatch(
              ONBOARDING_ACTIONS.SET_ONBOARDING_SENSOR_CAPABILITIES(
                seriesId,
                response.capabilities,
              ),
            );
          },
          () => {
            setExpiringGlobalStatus(
              dispatch,
              `Error getting capabilities for ${seriesId}`,
              'danger',
            );
          },
        ),
      );
    };
  },

  // ************************ Subscriptions ************************************
  /**
   * Retrieve the available subscriptions for given customerId
   */
  getSubscriptions: (customerId: string | null) => {
    return createThunk(
      SUBSCRIPTIONS_API.currentSubscriptions(customerId),
      'GET',
      null,
      CREDENTIAL_ACTIONS.SET_SUBSCRIPTIONS,
      (err, dispatch) => {
        setExpiringGlobalStatus(
          dispatch,
          `Error getting customer subscriptions. ${err.message}`,
          'danger',
        );
      },
    );
  },
};

/**
 * Performs a direct fetch against the API and returns a promise of the response without
 * any additional error handling or dispatching of the results.  Throws an APIError in the
 * event of request or authentication failure.
 */
export const apiRawFetch = {
  // ********** Credentials/Secrets API *********************************************************
  listCredentials: async () => {
    return await normalizeResponse<{ credentials: CredentialSecret[] }>(
      await doFetch(API.credentials, 'GET'),
    );
  },

  getCredential: async (uuid: string) => {
    return await normalizeResponse<{ credential: CredentialSecret }>(
      await doFetch(`${API.credentials}/${uuid}`, 'GET'),
    );
  },

  /**
   * Creates a new customer secret/credential for Cloud Sensors etc.
   * @param {CredentialSecret} credential - the credential to create
   * @return {Promise<{CredentialSecret}>} promise of newly-created credential
   */
  createCredential: async (credential: CredentialRequest) => {
    return await normalizeResponse<{ credential: CredentialSecret }>(
      await doFetch(API.credentials, 'POST', credential),
    );
  },

  /**
   * Edits an existing customer secret/credential for Cloud Sensors etc.
   * @param {string } uuid - UUID of the existing credential to edit
   * @param {CredentialRequest} credential - the credential to edit
   * @return {Promise<{CredentialSecret}>} promise of edited result
   */
  editCredential: async (uuid: string, credential: CredentialRequest) => {
    return await normalizeResponse<{ credential: CredentialSecret }>(
      await doFetch(`${API.credentials}/${uuid}`, 'PUT', credential),
    );
  },

  /**
   * Delete (or mark for deletion) the supplied secret
   * @param {CredentialSecret} credential - the secret to delete
   * @return {Promise<{ticket_id: ?number}>}
   * @throws APIError
   */
  deleteCredential: async (credential: CredentialSecret) => {
    return await normalizeResponse<{ ticket_id: number }>(
      await doFetch(
        `${API.credentials}/${credential.uuid}`,
        'DELETE',
        credential,
      ),
    );
  },

  // ********** Virtual Sensor licenses API *********************************************************
  listVirtualSensors: async () =>
    await normalizeResponse<VirtualSensor>(
      await doFetch(API.virtualSensors, 'GET'),
    ),

  // ********** Virtual Log Collector API *********************************************************
  listVirtualLogCollectors: async () =>
    await normalizeResponse<VirtualLogCollector[]>(
      await doFetch(API.virtualLogCollectors, 'GET'),
    ),

  decommissionLogCollector: async (vlc: VirtualLogCollector) =>
    await normalizeResponse(
      await doFetch(`${API.provisionSensor}/${vlc.serial_number}`, 'DELETE', {
        deployment: vlc.deployment,
      }),
    ),

  registerLogCollector: async (vlc: VirtualLogCollector) =>
    await normalizeResponse(
      await doFetch(`${API.provisionSensor}/${vlc.serial_number}`, 'POST', {
        shorthand: vlc.short_name,
        type: vlc.product_type,
        ...(vlc.profile_id && { profile_id: vlc.profile_id }),
        ...(vlc.hardware_type && { model: vlc.hardware_type }),
        ...(vlc.model_size &&
          vlc.model_size !== 'NA' && { model_size: vlc.model_size }),
        ...(vlc.site && { site: vlc.site }),
      }),
    ),

  /**
   * Requests generation of an installer from porthole, that when completed returns an object containing
   * an S3 link to the download and SHA256 to verify its contents.
   * @param {string} type - the type of installer to generate
   * @param {object} params - parameters required to generate installer depending on type
   * @return Promise<{sha256:string, url: string}> of download URL and file hash when complete, otherwise excepts with error msg
   */
  generateInstaller: async (
    type: InstallerType,
    params: {
      [key: string]: any;
    },
  ): Promise<{ sha256: string; url: string }> => {
    return await normalizeResponse(
      await doFetch(API.installer, 'POST', { ...params, type }),
    );
  },

  /**
   * Deactivate an offline agent.
   */
  deactivateAgentById: async (agentId: string) => {
    return await normalizeResponse(
      await doFetch(API.agents, 'DELETE', { agentId }),
    );
  },

  getAvailableAgents: async () => {
    return await normalizeResponse<{ platforms: AgentPlatform[] }>(
      await doFetch(API.availableAgentPlatforms, 'GET'),
    );
  },

  /**
   * Get a short-lived link to archived export results
   * @return Promise<object> of the url to access the results
   */
  getExportDownloadLink: async (searchExport: Export) => {
    return await normalizeResponse<{ url: string }>(
      await doFetch(
        `${API.logSearch}/${searchExport.searchId}/exports/${searchExport.id}/download`,
        'GET',
      ),
    );
  },

  // ********** ES Data API *********************************************************

  async getApiData<D>(
    type: string,
    filters: URLSearchParams,
    endpoint: string,
  ): Promise<D> {
    const params = new URLSearchParams(filters.toString());
    const range = getURLDateRangeAPI(
      filters.get(COMMON_DATA_FILTERS.START),
      filters.get(COMMON_DATA_FILTERS.END),
    );

    params.set('type', type);
    params.set('start', range.start);
    params.set('end', range.end);

    return await normalizeResponse<D>(
      await doFetch(`${endpoint}?${params.toString()}`, 'GET'),
    );
  },

  // ********** Log Search API V2 ************************************************************

  /**
   * Get exported files
   */
  getExportedResult: async (searchId?: string, exportId?: string) => {
    return normalizeResponse<ExportResult>(
      await doFetch(
        `${APIV2.logSearch}/${searchId}/exports/${exportId}`,
        'GET',
      ),
    );
  },

  /**
   * Get a single download link
   */
  getDownloadLink: async (
    searchId?: string,
    exportId?: string,
    partId?: string,
  ) => {
    return normalizeResponse<DownloadLink>(
      await doFetch(
        `${APIV2.logSearch}/${searchId}/exports/${exportId}/download-link/${partId}`,
        'GET',
      ),
    );
  },

  // ********** PRP API *******************************************************************
  /**
   * Retrieve the list of IPs based on region
   */
  getAWNIps: async (pod: string) => {
    return normalizeResponse<RawIPListType>(
      await doFetch(PRP_API_V2.ips(pod), 'GET', undefined, undefined, true),
    );
  },

  // *********************** Salesforce *********************************
  /**
   * Get active and future subscriptions of a given customer
   * @param customerId
   */
  getSalesforceSubscriptions: async (customerId: string | null) => {
    return normalizeResponse<SFSubscriptions>(
      await doFetch(SALESFORCE_API.subscriptions(customerId), 'GET'),
    );
  },

  // ************************ Generic Series ************************************
  getRecommendedModel: async (
    seriesId: string,
    capabilities: Record<string, string>,
  ) => {
    /***
     * converts all string values of "true" and "false" in a object to
     * there corresponding boolean values
     */
    const stringToBoolean = (
      object: Record<string, string>,
    ): Record<string, string | boolean> => {
      const transformed: Record<string, string | boolean> = {};
      for (const key in object) {
        if (object.hasOwnProperty(key)) {
          const b = object[key].toLowerCase();
          if (b === 'true' || b === 'false') {
            transformed[key] = b === 'true';
          } else {
            transformed[key] = object[key];
          }
        }
      }
      return transformed;
    };

    if (seriesId) {
      return normalizeResponse<SensorRecommendations>(
        await doFetch(
          GENERIC_SERIES_API.models(seriesId),
          'POST',
          stringToBoolean(capabilities),
        ),
      );
    }
  },

  /************************** Status Page APIs *****************************/

  getSiteSensorProfiles: async () => {
    return normalizeResponse<SiteSensorProfiles>(
      await doFetch(API.siteSensorProfiles, 'GET'),
    );
  },

  getAgents: async () => {
    return normalizeResponse<{ data: RawAgent[] }>(
      await doFetch(API.agents, 'GET'),
    );
  },
};
